/**
 * Tilda forms don't fire native submit-event — Tilda's jQuery handler catches
 * the click on the submit button, runs its own validation (incl. the
 * "выберите автобус" captcha), and POSTs via fetch. So `document.addEventListener('submit', ...)`
 * never sees anything.
 *
 * Tilda DOES, however, expose form-specific success callbacks declared on the
 * form itself (`data-success-callback`, `data-formsended-callback`). Their
 * values point to global functions like `t702_onSuccess`, `t706_onSuccessCallback`,
 * `tNNN_onSuccess` — sometimes defined right at page-load time, sometimes after.
 *
 * This module:
 *   1. detects "this is a Tilda page" by looking for `.t-form` or `.js-form-proccess`
 *   2. collects every callback name referenced by Tilda forms on the page
 *   3. wraps each callback so it emits form_submit before delegating to Tilda's
 *      original implementation. If the callback isn't defined yet, we re-poll
 *      for a few seconds before giving up.
 *
 * Idempotent: if wrap-already-applied we skip. Safe to call multiple times.
 */
import type { Json } from "../types";

export interface TildaHookOptions {
  emit: (eventName: string, props: Record<string, Json>) => void;
}

const WRAPPED = Symbol.for("xpixel.tilda.wrapped");
const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 250;

type WrappedFn = ((...args: unknown[]) => unknown) & { [WRAPPED]?: true };


function findTildaCallbackNames(): Map<string, string> {
  // form_id → callback_name
  const out = new Map<string, string>();
  const forms = document.querySelectorAll<HTMLFormElement>(
    "form.t-form, form.js-form-proccess, form[data-success-callback], form[data-formsended-callback]",
  );
  forms.forEach((form) => {
    const id = form.id || form.getAttribute("name") || "tilda_form";
    const cb =
      form.getAttribute("data-success-callback") ||
      form.getAttribute("data-formsended-callback");
    if (cb) {
      out.set(id, cb);
    }
  });
  return out;
}


function isTildaPage(): boolean {
  return document.querySelector(".t-form, .js-form-proccess") !== null;
}


function fieldsCount(form: HTMLFormElement | null): number {
  if (!form) return 0;
  return form.querySelectorAll(
    "input:not([type=hidden]):not([type=submit]):not([type=button]):not([type=reset]), textarea, select",
  ).length;
}


function wrapOne(
  callbackName: string,
  formId: string,
  emit: TildaHookOptions["emit"],
): boolean {
  // Tilda stores its callbacks on window. We replace the global with a wrapper.
  const w = window as unknown as Record<string, WrappedFn | undefined>;
  const original = w[callbackName];

  if (typeof original === "function" && original[WRAPPED]) {
    // Already wrapped by us — nothing to do.
    return true;
  }
  if (typeof original !== "function") {
    return false;
  }

  const wrapped: WrappedFn = function (this: unknown, ...args: unknown[]) {
    try {
      const formArg = args[0];
      const form: HTMLFormElement | null =
        formArg instanceof HTMLFormElement
          ? formArg
          : (document.getElementById(formId) as HTMLFormElement | null);
      emitSubmitOnce(emit, form, formId, "tilda_callback", { callback_name: callbackName });
    } catch {
      // never throw out of pixel
    }
    return original.apply(this, args);
  };
  wrapped[WRAPPED] = true;
  w[callbackName] = wrapped;
  return true;
}


// One form_submit per form per few seconds, whichever hook fires first
// (callback wrapper, tildaform:aftersuccess, tildaForm.successEnd).
const recentSubmits = new Map<string, number>();
const DEDUPE_WINDOW_MS = 3000;

function emitSubmitOnce(
  emit: TildaHookOptions["emit"],
  form: HTMLFormElement | null,
  formId: string,
  via: string,
  extra: Record<string, Json> = {},
): void {
  const now = Date.now();
  const last = recentSubmits.get(formId) || 0;
  if (now - last < DEDUPE_WINDOW_MS) return;
  recentSubmits.set(formId, now);
  emit("form_submit", {
    form_id: formId,
    form_method: (form && form.method) || "post",
    fields_count: fieldsCount(form),
    via,
    ...extra,
  });
}

function formIdOf(el: unknown): { form: HTMLFormElement | null; id: string } {
  const form = el instanceof HTMLFormElement ? el : null;
  return { form, id: (form && (form.id || form.getAttribute("name"))) || "tilda_form" };
}

/**
 * Generic hooks that cover every Tilda form type, including Zero Block forms
 * whose success callback isn't declared on the <form> (hellopark-avk.ru/dr:
 * Tilda showed "Спасибо", we only saw form_start).
 * tilda-forms-1.0.js does `t_triggerEvent(form, "tildaform:aftersuccess")`
 * and `window.tildaForm.successEnd(form, url, cb)` on every success.
 */
function installGenericTildaHooks(emit: TildaHookOptions["emit"]): () => void {
  const onAfterSuccess = (ev: Event) => {
    const { form, id } = formIdOf(ev.target);
    emitSubmitOnce(emit, form, id, "tilda_event");
  };
  // capture=true: catches the event even if Tilda dispatches it non-bubbling.
  document.addEventListener("tildaform:aftersuccess", onAfterSuccess, true);

  let attempts = 0;
  const timer = window.setInterval(() => {
    attempts += 1;
    const tf = (window as unknown as { tildaForm?: Record<string, unknown> }).tildaForm;
    const original = tf && (tf.successEnd as WrappedFn | undefined);
    if (typeof original === "function") {
      clearInterval(timer);
      if (original[WRAPPED]) return;
      const wrapped: WrappedFn = function (this: unknown, ...args: unknown[]) {
        try {
          const { form, id } = formIdOf(args[0]);
          emitSubmitOnce(emit, form, id, "tilda_success_end");
        } catch {
          // never throw out of pixel
        }
        return original.apply(this, args);
      };
      wrapped[WRAPPED] = true;
      (tf as Record<string, unknown>).successEnd = wrapped;
    } else if (attempts >= MAX_POLL_ATTEMPTS * 4) {
      clearInterval(timer);
    }
  }, POLL_INTERVAL_MS);

  return () => {
    document.removeEventListener("tildaform:aftersuccess", onAfterSuccess, true);
    clearInterval(timer);
  };
}


export function installTildaFormHooks(opts: TildaHookOptions): () => void {
  let cancelled = false;
  const stopGeneric = installGenericTildaHooks(opts.emit);

  function tryHookAll(): { hooked: number; pending: number } {
    const names = findTildaCallbackNames();
    let hooked = 0;
    let pending = 0;
    names.forEach((callbackName, formId) => {
      if (wrapOne(callbackName, formId, opts.emit)) {
        hooked += 1;
      } else {
        pending += 1;
      }
    });
    return { hooked, pending };
  }

  function start() {
    if (!isTildaPage()) return;
    let { pending } = tryHookAll();
    let attempts = 0;

    if (pending === 0) return;

    // Tilda may register its callbacks asynchronously after their inline
    // <script> chunk finishes loading. Poll for a few seconds.
    const id = window.setInterval(() => {
      if (cancelled) {
        clearInterval(id);
        return;
      }
      const result = tryHookAll();
      attempts += 1;
      if (result.pending === 0 || attempts >= MAX_POLL_ATTEMPTS) {
        clearInterval(id);
      }
    }, POLL_INTERVAL_MS);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }

  return () => {
    cancelled = true;
    stopGeneric();
  };
}
